"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SyncStoreLinkResponse = exports.StorePass = exports.StoreLinkEndpoint = exports.UpdateStoreLinkSettingsRequest = exports.StoreLinkSettingsDelivered = exports.EXPORT_OWNER_PERIODS_MAX = exports.ExportOwnerPeriod = exports.UpdateExternalRefundPathRequest = exports.EXTERNAL_REFUND_PATH_ABSENT_CONFIRMATION = exports.ExternalRefundPath = exports.EXTERNAL_REFUND_PATHS = exports.STORE_LINK_DEFAULT_SETTINGS = exports.StoreLinkSettings = exports.STORE_PASS_LIFETIME_DEFAULT_MINUTES = exports.STORE_PASS_LIFETIME_MAX_MINUTES = exports.STORE_PASS_LIFETIME_MIN_MINUTES = exports.ConnectorLoyaltySource = exports.CONNECTOR_LOYALTY_SOURCES = exports.ConnectorExportOwner = exports.CONNECTOR_EXPORT_OWNERS = exports.ConnectorCatalogueSource = exports.CONNECTOR_CATALOGUE_SOURCES = void 0;
exports.loyaltySourceOf = loyaltySourceOf;
exports.storeUsesLink = storeUsesLink;
exports.exportOwnerAt = exportOwnerAt;
exports.deliveredExportOwnerPeriods = deliveredExportOwnerPeriods;
const zod_1 = require("zod");
const link_certificate_js_1 = require("./link-certificate.js");
/**
 * `sync.store-link` (RL-452, ADR-0014 §5) — one store's connector ownership,
 * served by Back Office to its tills.
 *
 * A store has exactly one owner per direction, so receipt numbering and the
 * record of what was sent live in one place:
 *
 * - `catalogueSource` — where the till fetches the connector-owned catalogue
 *   and cashier feeds: Back Office, or the store's Link;
 * - `exportOwner` — who writes the store's documents into its own system:
 *   nobody, Back Office, or the store's Link.
 *
 * The settings are authored per location in Back Office. An absent row means
 * the defaults below, which are exactly what every store did before Link
 * existed, so a till that pulls this domain against an unconfigured store
 * changes nothing.
 */
exports.CONNECTOR_CATALOGUE_SOURCES = ['backoffice', 'link'];
exports.ConnectorCatalogueSource = zod_1.z.enum(exports.CONNECTOR_CATALOGUE_SOURCES);
exports.CONNECTOR_EXPORT_OWNERS = ['off', 'backoffice', 'link'];
exports.ConnectorExportOwner = zod_1.z.enum(exports.CONNECTOR_EXPORT_OWNERS);
/**
 * RL-569 — where the store's tills resolve loyalty customers:
 *
 * - `backoffice` — Back Office's `GET /loyalty/lookup` (and re:form Loyalty
 *   behind it), what every store did before this setting existed;
 * - `link` — the store's Link, which asks the store's CRM loyalty source
 *   (integration profile `ip-3`, "CRM loyalty") over the store network. For
 *   a CRM that can be reached only from inside the retailer's network.
 *
 * Optional on the wire everywhere it travels (`sync.store-link`,
 * `/link/config`, the settings PUT): absent reads as `backoffice`, so an older
 * Back Office changes nothing on a newer till or Link. Read it through
 * {@link loyaltySourceOf}, never as a bare field.
 */
exports.CONNECTOR_LOYALTY_SOURCES = ['backoffice', 'link'];
exports.ConnectorLoyaltySource = zod_1.z.enum(exports.CONNECTOR_LOYALTY_SOURCES);
/**
 * Store-pass lifetime bounds, in minutes: one hour to thirty days. Counted
 * from the last time the till was online, so this is how long a till may keep
 * using its Link while Back Office is unreachable.
 */
exports.STORE_PASS_LIFETIME_MIN_MINUTES = 60;
exports.STORE_PASS_LIFETIME_MAX_MINUTES = 43_200;
exports.STORE_PASS_LIFETIME_DEFAULT_MINUTES = 4_320;
exports.StoreLinkSettings = zod_1.z.object({
    catalogueSource: exports.ConnectorCatalogueSource,
    /** SSC-229: independent of connector catalogue/export routing; absent means BOS. */
    secureScaleSource: exports.ConnectorCatalogueSource.optional(),
    exportOwner: exports.ConnectorExportOwner,
    storePassLifetimeMinutes: zod_1.z
        .number()
        .int()
        .min(exports.STORE_PASS_LIFETIME_MIN_MINUTES)
        .max(exports.STORE_PASS_LIFETIME_MAX_MINUTES),
    /** RL-569. Absent = `backoffice`; see {@link ConnectorLoyaltySource}. */
    loyaltySource: exports.ConnectorLoyaltySource.optional(),
});
exports.STORE_LINK_DEFAULT_SETTINGS = {
    catalogueSource: 'backoffice',
    exportOwner: 'off',
    storePassLifetimeMinutes: exports.STORE_PASS_LIFETIME_DEFAULT_MINUTES,
    loyaltySource: 'backoffice',
};
/** RL-569: the store's loyalty source, with absent read as `backoffice`. */
function loyaltySourceOf(settings) {
    return settings.loyaltySource ?? 'backoffice';
}
/**
 * Whether the store's Link serves its tills anything at all — the ONE rule
 * for "does this store use its Link", which decides whether Back Office
 * issues a till a store pass and whether Link must refuse every outstanding
 * pass for the store (`GET /link/revoked-terminals`). A till applies the same
 * rule to decide whether it needs a pass.
 *
 * - `catalogueSource = link` (RL-452): the till pulls the catalogue there;
 * - `exportOwner = link` (RL-452): it uploads documents there;
 * - `loyaltySource = link` (RL-569): it resolves loyalty customers there;
 * - `secureScaleSource = link` (SSC-229): it serves the offline security replica;
 * - [linkOwnsEarlierDocuments] (RL-460): the Link owned the export in an
 *   earlier period and still receives that period's late documents;
 * - [linkAnswersLiveLookups] (RL-769): the store's Link holds a source its
 *   tills question directly while a sale is open (a live-lookup profile),
 *   whoever owns the catalogue and the export. Not a setting — there is no
 *   other host that could answer — but a fact of the store's sources, which
 *   only Back Office knows; a till learns it by being issued a pass.
 *
 * The last two are facts beside the settings, so a caller that knows them
 * passes them; a caller that does not (a till) asks about the settings alone.
 *
 * Every reader that asks the question goes through here, so a new way of
 * using the Link is added in one place and not to one gate and missed at the
 * next.
 */
function storeUsesLink(settings, linkOwnsEarlierDocuments = false, linkAnswersLiveLookups = false) {
    return (settings.catalogueSource === 'link' ||
        settings.exportOwner === 'link' ||
        loyaltySourceOf(settings) === 'link' ||
        settings.secureScaleSource === 'link' ||
        linkOwnsEarlierDocuments ||
        linkAnswersLiveLookups);
}
/**
 * RL-847 §5.2 (Q6) — the foreign-register refund fact of one store, authored
 * per location by an operator with the connectors settings grant, and audited:
 *
 * - `unknown` — the default; treated as `present`;
 * - `present` — a register outside re:form can refund this store's re:form
 *   sales (through the store system, say) and Back Office never sees it;
 * - `absent` — the person who can vouch for the store's fleet states there
 *   is no such register. Accepted only with
 *   {@link EXTERNAL_REFUND_PATH_ABSENT_CONFIRMATION}, never inferred from
 *   configuration.
 *
 * Back Office serves card provenance for a sale only while its SELLING store
 * is `absent`, and only for sales finalised at or after the store last became
 * `absent`. A till's Varus consumer (step 6) acts only when its own store is
 * `absent`. The terminals mirror reads anything else as `unknown`.
 */
exports.EXTERNAL_REFUND_PATHS = ['unknown', 'present', 'absent'];
exports.ExternalRefundPath = zod_1.z.enum(exports.EXTERNAL_REFUND_PATHS);
/** The words an operator confirms before a store is recorded as `absent`. */
exports.EXTERNAL_REFUND_PATH_ABSENT_CONFIRMATION = "No register outside re:form can refund this store's sales";
/**
 * `PUT /connectors/locations/:locationId/external-refund-path`. Strict, and
 * separate from the ownership PUT ({@link UpdateStoreLinkSettingsRequest}),
 * so no screen that saves the ownership can author it by accident.
 */
exports.UpdateExternalRefundPathRequest = zod_1.z
    .object({
    externalRefundPath: exports.ExternalRefundPath,
    /** Required, word for word, for `absent`; ignored otherwise. */
    confirmation: zod_1.z.string().max(200).optional(),
})
    .strict()
    .superRefine((request, ctx) => {
    if (request.externalRefundPath === 'absent' &&
        request.confirmation !== exports.EXTERNAL_REFUND_PATH_ABSENT_CONFIRMATION) {
        ctx.addIssue({
            code: 'custom',
            path: ['confirmation'],
            message: `absent needs the confirmation "${exports.EXTERNAL_REFUND_PATH_ABSENT_CONFIRMATION}"`,
        });
    }
});
/**
 * RL-460 — who owned the store's documents, and since when.
 *
 * THE RULE (Back Office, Link and the till apply it alike): a document
 * belongs to the export owner in force at the document's creation time. The
 * owner at instant t is the owner of the latest period whose `since` is at or
 * before t. So each host exports only the documents created in its own
 * periods — including documents that reach it long after it stopped being the
 * owner — and a switch never leaves a document to nobody or to both.
 *
 * Creation time: exactly one timestamp INSIDE the uploaded body per kind,
 * so the till and both hosts decide on the same value:
 *
 *   receipt        `/sync/transactions*`  record `finalisedAt`
 *   cash movement  `/sync/shift-events`   `cash-movement` event `occurredAt` (the envelope's, not `payload.occurredAt`)
 *   opening float  `/sync/shift-events`   `shift.open` event `occurredAt`
 *   Z report       `/sync/shift-events`   `shift.close` event `occurredAt`
 *
 * (Back Office stores those as `receipts.finalisedAt`, `cash_movements.occurredAt`,
 * `shifts.openedAt` and `shifts.closedAt`.) Other shift events carry no
 * document of their own: a till sends them to Link whenever it sends that
 * shift's documents there. On doubt a till sends to Link as well; a document
 * both hosts end up writing carries the same `reform:<terminalId>:<clientUuid>`
 * marker, so the second write finds the first and is `already_present`.
 *
 * `since: null` is the first period: from the beginning, i.e. the owner the
 * store had before its first recorded change.
 *
 * `off` (RL-460 review): a document created while the store had no export
 * owner belongs to the owner of the next period that has one, as Back Office
 * treated those documents before ownership was recorded; while the store is
 * still `off` it belongs to nobody yet, and no host sends anything. A till
 * with a Link therefore also sends the Link what it creates while `off` —
 * the Link may become the next owner.
 */
exports.ExportOwnerPeriod = zod_1.z.object({
    owner: exports.ConnectorExportOwner,
    since: zod_1.z.string().datetime().nullable(),
});
/**
 * RL-460 follow-up — the most ownership periods `sync.store-link` carries.
 * The same bound a till keeps in its own ledger. See
 * `deliveredExportOwnerPeriods` for why a history cut to it is still safe.
 */
exports.EXPORT_OWNER_PERIODS_MAX = 64;
/**
 * The settings as Back Office delivers them: the authored row plus
 * `exportOwnerSince`, ISO-8601 UTC, the instant the CURRENT `exportOwner`
 * took effect. Set when `exportOwner` changes and never moved while the owner
 * stays the same. Null — or absent, from an older Back Office — when the
 * current owner has held the store since before owner changes were recorded;
 * a till then dates the period from the moment it first saw that owner.
 *
 * `exportOwnerPeriods` (RL-460 follow-up): the store's ownership history,
 * oldest first, built by `deliveredExportOwnerPeriods` — the same list
 * `/link/config` carries, cut to `EXPORT_OWNER_PERIODS_MAX`. With it a till
 * that was offline for a whole Link period, or never pulled this domain
 * while it lasted, still learns that period and copies its documents to the
 * Link; with `exportOwnerSince` alone it only ever sees the owner of now.
 * The last period is the current `exportOwner`. Absent from an older Back
 * Office: a till then builds its ledger from what it observes, as before.
 */
exports.StoreLinkSettingsDelivered = exports.StoreLinkSettings.extend({
    exportOwnerSince: zod_1.z.string().datetime().nullable().optional(),
    exportOwnerPeriods: zod_1.z.array(exports.ExportOwnerPeriod).max(exports.EXPORT_OWNER_PERIODS_MAX).optional(),
    /**
     * RL-847 §5.2 — whether a register outside re:form can refund this store's
     * sales without Back Office seeing the refund. On `sync.store-link` only
     * (never on `/link/config`), and only once an operator authored a value
     * other than `unknown`: absent reads as `unknown`, which is treated as
     * `present`, so an older Back Office keeps the till's card-refund consumer
     * off. Inert until RL-847 step 6. See {@link ExternalRefundPath}.
     */
    externalRefundPath: exports.ExternalRefundPath.optional(),
});
/**
 * The owner of a document created at [at] (RL-460) — see `ExportOwnerPeriod`.
 * [periods] in any order; with none recorded, [current] owns every document.
 */
function exportOwnerAt(periods, current, at) {
    if (periods.length === 0)
        return current;
    const sinceOf = (period) => period.since === null ? -Infinity : Date.parse(period.since);
    const ordered = [...periods].sort((a, b) => sinceOf(a) - sinceOf(b));
    let index = -1;
    ordered.forEach((period, i) => {
        if (sinceOf(period) <= at.getTime())
            index = i;
    });
    if (index < 0)
        return 'off';
    // RL-460 review: a document created while the export was `off` belongs to
    // the next owner the store is given; until there is one, to nobody yet.
    for (let i = index; i < ordered.length; i += 1) {
        if (ordered[i].owner !== 'off')
            return ordered[i].owner;
    }
    return 'off';
}
/**
 * The ownership history as Back Office delivers it — on `/link/config` and,
 * cut to [max], on `sync.store-link` (RL-460 follow-up). [periods] in any
 * order; the result is oldest first.
 *
 * 1. **From the earliest still-relevant period** (lossless). Before the
 *    store's first `link` period no document can be the Link's except by an
 *    `off` run resolving forward into it. So every period before the last
 *    `backoffice` period that precedes the first `link` one is dropped, and
 *    that `backoffice` period becomes the first (`since: null`). The dropped
 *    periods resolve, under `exportOwnerAt`, to `backoffice` or to that same
 *    period, so every instant keeps its owner. That
 *    is what keeps a store that ran on Back Office for years, then moved to
 *    Link, at two periods. Only a history that starts from the beginning is
 *    compacted: one that does not has an instant before it that is nobody's.
 * 2. **At most [max] periods** (a store switched back and forth more than 30
 *    times). The oldest go, and the first one kept keeps its real `since`, so
 *    a reader sees where the history stops. An `off` run the cut would drop
 *    right before it resolves forward into that period, so the period is
 *    dated from the run's start instead: no instant changes owner, and none
 *    before the cut depends on what was kept. Before it the history is silent,
 *    and `exportOwnerAt` answers `off`. A till reads that as "no answer" and
 *    uses its own recorded periods there. A document that old was
 *    uploaded dozens of guarded switches ago; each switch waited for the
 *    previous owner's queue to drain. `/link/config` is not cut (`max`
 *    Infinity): the Link is the only host that exports its periods, and a
 *    history it lacked would lose those documents rather than duplicate them.
 */
function deliveredExportOwnerPeriods(periods, max = exports.EXPORT_OWNER_PERIODS_MAX) {
    const sinceOf = (period) => period.since === null ? -Infinity : Date.parse(period.since);
    const ordered = [...periods]
        .sort((a, b) => sinceOf(a) - sinceOf(b))
        .map((period) => ({ owner: period.owner, since: period.since }));
    let history = ordered;
    if (ordered.length > 0 && ordered[0].since === null) {
        const firstLink = ordered.findIndex((period) => period.owner === 'link');
        const end = firstLink < 0 ? ordered.length : firstLink;
        let anchor = -1;
        for (let i = 0; i < end; i += 1)
            if (ordered[i].owner === 'backoffice')
                anchor = i;
        if (anchor > 0) {
            history = [{ owner: 'backoffice', since: null }, ...ordered.slice(anchor + 1)];
        }
    }
    if (history.length <= max)
        return history;
    // An `off` run straight before the cut resolves forward into the first
    // period kept, so it is that period's already: the first period kept
    // starts where the run does. Every instant from there on keeps its owner,
    // and the part dropped ends on an owner, so it decides its own instants.
    const cut = history.length - max;
    let start = cut;
    while (start > 0 && history[start - 1].owner === 'off')
        start -= 1;
    const kept = history.slice(cut);
    if (start < cut)
        kept[0] = { owner: kept[0].owner, since: history[start].since };
    return kept;
}
/**
 * `PUT /connectors/locations/:locationId/settings`. The whole row, strict: an
 * operator changing ownership states all three values, and a typo'd key is an
 * error rather than a silently kept old value.
 *
 * RL-569: `loyaltySource` is the one optional key. Absent keeps the stored
 * value, so a Back Office screen built before it cannot reset a store's
 * loyalty routing by saving its other settings. Back Office refuses `link`
 * unless the store has an active Link and an enabled `ip-3` source.
 */
exports.UpdateStoreLinkSettingsRequest = exports.StoreLinkSettings.strict();
/** Where the store's Link answers on the LAN. Null while the store has none. */
exports.StoreLinkEndpoint = zod_1.z.object({
    url: zod_1.z.string().url(),
    /**
     * `sha256:<64 hex>` of the DER leaf certificate, current first; the next one
     * is added during a rollover (RL-457). A till accepts the Link's TLS
     * connection if and only if the leaf it presents matches one of these — see
     * `link-certificate.ts` for the whole pinning contract.
     */
    certificateFingerprints: zod_1.z.array(link_certificate_js_1.CertificateFingerprint).min(1).max(2),
    /** The Link's own terminal id. */
    terminalId: zod_1.z.string(),
});
/**
 * The till's credential towards its Link: a compact EdDSA JWS. Null unless
 * the store uses its Link ({@link storeUsesLink}): its catalogue, export or
 * (RL-569) loyalty is the Link's, the Link still owns documents of an
 * earlier export period, or (RL-769) the Link answers the tills' live
 * lookups.
 */
exports.StorePass = zod_1.z.object({
    token: zod_1.z.string().min(1),
    issuedAt: zod_1.z.string().datetime(),
    expiresAt: zod_1.z.string().datetime(),
    /** A till renews on its ordinary sync once `now >= renewAfter`. */
    renewAfter: zod_1.z.string().datetime(),
});
/**
 * `GET /sync/store-link`. One object, no pagination; the calling till's own
 * store only. `link` and `storePass` are filled by later tickets (RL-450,
 * RL-453) and are null until then.
 */
exports.SyncStoreLinkResponse = zod_1.z.object({
    /** Authenticated terminal location; optional for older producers. */
    locationId: zod_1.z.string().min(1).nullable().optional(),
    /** RL-460: with `exportOwnerSince`, so a till routes each document by the same rule. */
    settings: exports.StoreLinkSettingsDelivered,
    link: exports.StoreLinkEndpoint.nullable(),
    storePass: exports.StorePass.nullable(),
    /**
     * RL-847 O8(b): what Back Office knows of this store's Link build. Absent
     * from an older Back Office, null when the store has no active Link device.
     * A fact Back Office observes, not a setting: the operator's PUT never
     * carries it. Bound to one device, and versionless on purpose — the till
     * compares no versions. Not served before RL-847 step 2c.
     */
    linkSupport: zod_1.z
        .object({
        /** The Link device this statement is about (= `link.terminalId`). */
        linkTerminalId: zod_1.z.string().min(1),
        /**
         * Whether that Link accepts `POST /sync/transactions/card-provenance`,
         * combined with extensions, under L1–L8.
         */
        cardProvenanceV1: zod_1.z.enum(['ready', 'not_ready', 'unknown']),
    })
        .strict()
        .nullable()
        .optional(),
});
//# sourceMappingURL=store-link.js.map